// Connexion par empreinte digitale (WebAuthn).
//
// Le navigateur échange des `ArrayBuffer`, l'API du JSON : ce module ne fait que
// traduire dans les deux sens et enchaîner les deux appels réseau. Toute la
// vérification est côté serveur — ici, rien n'est une preuve.
//
// ⚠️ Ce fichier existe en DEUX COPIES IDENTIQUES :
//   - packages/auth/src/services/webauthn.js     (connexion)
//   - packages/portail/src/services/webauthn.js  (enrôlement et gestion)
// Les deux fronts sont des CRA indépendants sans paquet commun — même situation que
// `VersionApp` et `BonhommeNav`. Toute modification doit être portée dans les deux.

const API = process.env.REACT_APP_API_AUTH || "/api/auth";

// --- base64url <-> ArrayBuffer ---

function base64urlVersBuffer(valeur) {
  const base64 = valeur.replace(/-/g, "+").replace(/_/g, "/");
  const rembourre = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  const binaire = atob(rembourre);
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  return octets.buffer;
}

function bufferVersBase64url(buffer) {
  const octets = new Uint8Array(buffer);
  let binaire = "";
  for (let i = 0; i < octets.length; i += 1) binaire += String.fromCharCode(octets[i]);
  return btoa(binaire).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Décode les champs binaires des options renvoyées par le serveur.
 * `navigator.credentials` refuse des chaînes là où il attend des `BufferSource`.
 */
function preparerOptions(options) {
  const prets = { ...options, challenge: base64urlVersBuffer(options.challenge) };
  if (options.user) {
    prets.user = { ...options.user, id: base64urlVersBuffer(options.user.id) };
  }
  ["excludeCredentials", "allowCredentials"].forEach((cle) => {
    if (Array.isArray(options[cle])) {
      prets[cle] = options[cle].map((c) => ({ ...c, id: base64urlVersBuffer(c.id) }));
    }
  });
  return prets;
}

/** Sérialise la réponse d'enrôlement comme l'attend le serveur. */
function serialiserCreation(credential) {
  return {
    id: credential.id,
    rawId: bufferVersBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment || null,
    clientExtensionResults: credential.getClientExtensionResults
      ? credential.getClientExtensionResults()
      : {},
    response: {
      clientDataJSON: bufferVersBase64url(credential.response.clientDataJSON),
      attestationObject: bufferVersBase64url(credential.response.attestationObject),
      transports: credential.response.getTransports
        ? credential.response.getTransports()
        : [],
    },
  };
}

/** Sérialise l'assertion de connexion comme l'attend le serveur. */
function serialiserAssertion(credential) {
  return {
    id: credential.id,
    rawId: bufferVersBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment || null,
    clientExtensionResults: credential.getClientExtensionResults
      ? credential.getClientExtensionResults()
      : {},
    response: {
      clientDataJSON: bufferVersBase64url(credential.response.clientDataJSON),
      authenticatorData: bufferVersBase64url(credential.response.authenticatorData),
      signature: bufferVersBase64url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? bufferVersBase64url(credential.response.userHandle)
        : null,
    },
  };
}

// --- disponibilité ---

/**
 * Vrai si l'appareil dispose d'un capteur intégré utilisable (empreinte, Face ID,
 * Windows Hello). Sans ce test, on afficherait le bouton sur un poste fixe sans
 * capteur, où il ne peut qu'échouer.
 *
 * `isUserVerifyingPlatformAuthenticatorAvailable` n'existe pas partout : son
 * absence est traitée comme « pas de capteur », jamais comme une erreur.
 */
export async function capteurDisponible() {
  if (typeof window === "undefined" || !window.PublicKeyCredential) return false;
  if (!window.isSecureContext) return false; // WebAuthn exige HTTPS (localhost excepté)
  const test = window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable;
  if (typeof test !== "function") return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_) {
    return false;
  }
}

/** Vrai si le serveur est configuré pour WebAuthn (RP ID renseigné). */
export async function serveurDisponible() {
  try {
    const reponse = await fetch(`${API}/api/webauthn/available`, { credentials: "include" });
    if (!reponse.ok) return false;
    const corps = await reponse.json();
    return Boolean(corps.available);
  } catch (_) {
    return false;
  }
}

/** Le bouton ne s'affiche que si l'appareil **et** le serveur suivent. */
export async function connexionEmpreinteDisponible() {
  const [capteur, serveur] = await Promise.all([capteurDisponible(), serveurDisponible()]);
  return capteur && serveur;
}

// --- connexion ---

/**
 * Ouvre une session par empreinte. Rend `{ ok: true }` ou `{ ok: false, message }`.
 *
 * `NotAllowedError` couvre l'annulation par l'utilisateur *et* l'expiration du
 * délai : ce n'est pas une erreur à signaler en rouge, on retombe simplement sur
 * le mot de passe.
 */
export async function seConnecterAvecEmpreinte() {
  let state;
  let options;
  try {
    const reponse = await fetch(`${API}/api/webauthn/login/options`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!reponse.ok) {
      return { ok: false, message: "La connexion par empreinte est indisponible." };
    }
    const corps = await reponse.json();
    state = corps.state;
    options = JSON.parse(corps.options);
  } catch (_) {
    return { ok: false, message: "Serveur injoignable." };
  }

  let credential;
  try {
    credential = await navigator.credentials.get({
      publicKey: preparerOptions(options),
    });
  } catch (erreur) {
    if (erreur && erreur.name === "NotAllowedError") {
      return { ok: false, annule: true };
    }
    return { ok: false, message: "Empreinte non reconnue sur cet appareil." };
  }
  if (!credential) return { ok: false, annule: true };

  try {
    const reponse = await fetch(`${API}/api/webauthn/login/verify`, {
      method: "POST",
      credentials: "include", // indispensable : c'est ici que les cookies sont posés
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        credential: serialiserAssertion(credential),
        state,
        remember: true,
      }),
    });
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      return {
        ok: false,
        message: corps.error || "Cet appareil n'est pas enregistré pour la connexion par empreinte.",
      };
    }
    return { ok: true };
  } catch (_) {
    return { ok: false, message: "Serveur injoignable." };
  }
}

// --- enrôlement (utilisé par le portail) ---

/** Enregistre l'appareil courant. Nécessite une session déjà ouverte. */
export async function enregistrerEmpreinte(libelleAppareil) {
  let state;
  let options;
  try {
    const reponse = await fetch(`${API}/api/webauthn/register/options`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    const corps = await reponse.json().catch(() => ({}));
    if (!reponse.ok) {
      return { ok: false, message: corps.error || "Enregistrement indisponible." };
    }
    state = corps.state;
    options = JSON.parse(corps.options);
  } catch (_) {
    return { ok: false, message: "Serveur injoignable." };
  }

  let credential;
  try {
    credential = await navigator.credentials.create({
      publicKey: preparerOptions(options),
    });
  } catch (erreur) {
    if (erreur && erreur.name === "NotAllowedError") {
      return { ok: false, annule: true };
    }
    // InvalidStateError = cet appareil est déjà enrôlé (excludeCredentials a joué)
    if (erreur && erreur.name === "InvalidStateError") {
      return { ok: false, message: "Cet appareil est déjà enregistré." };
    }
    return { ok: false, message: "Impossible d'utiliser le capteur de cet appareil." };
  }
  if (!credential) return { ok: false, annule: true };

  try {
    const reponse = await fetch(`${API}/api/webauthn/register/verify`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        credential: serialiserCreation(credential),
        state,
        device_label: libelleAppareil || libelleParDefaut(),
      }),
    });
    const corps = await reponse.json().catch(() => ({}));
    if (!reponse.ok) {
      return { ok: false, message: corps.error || "Enregistrement refusé." };
    }
    return { ok: true, device_label: corps.device_label };
  } catch (_) {
    return { ok: false, message: "Serveur injoignable." };
  }
}

/** Liste les appareils enregistrés du compte courant. */
export async function listerAppareils() {
  const reponse = await fetch(`${API}/api/webauthn/credentials`, { credentials: "include" });
  if (!reponse.ok) throw new Error("Lecture des appareils impossible");
  const corps = await reponse.json();
  return corps.credentials || [];
}

/** Retire un appareil enregistré. */
export async function supprimerAppareil(id) {
  const reponse = await fetch(`${API}/api/webauthn/credentials/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!reponse.ok) throw new Error("Suppression impossible");
  return true;
}

/** Libellé lisible par défaut, déduit de la plateforme — jamais du User-Agent complet. */
export function libelleParDefaut() {
  const ua = navigator.userAgent || "";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) return "Appareil Android";
  if (/Macintosh/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "PC Windows";
  return "Cet appareil";
}
